import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/apiFetch';
import { Database, FileText, Clock, AlertTriangle, Loader2, InboxIcon, Info, History, Search as SearchIcon, Trash2 } from 'lucide-react';
import AppNavActions from '../components/AppNavActions';
import Tooltip from '../components/Tooltip';
import { formatTimeAgo } from '../lib/timeAgo';
import { useApiRoot } from '../contexts/ApiRootContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type HalLink = { href: string };
type MyAraLinks = Record<string, HalLink>;

interface ProcessError {
  code?: string;
  message?: string;
  severity?: string;
}

interface UploadItem {
  id: string;
  'processing.suggested_title': string | null;
  status: string;
  initiated_at: string | null;
  completed_at: string | null;
  failed_at: string | null;
  errors: ProcessError[];
}

interface RecentRecord {
  id: string;
  title: string | null;
  timestamp: string | null;
}

interface RecentSearch {
  id: string;
  raw_query: string;
  started_at: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function fetchProcessList(endpoint: string): Promise<UploadItem[]> {
  const resp = await apiFetch(endpoint);
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const data = await resp.json();
  return (data?._embedded?.ingestion_processes ?? []) as UploadItem[];
}

async function fetchEmbeddedList<T>(endpoint: string, key: string): Promise<T[]> {
  const resp = await apiFetch(endpoint);
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const data = await resp.json();
  return (data?._embedded?.[key] ?? []) as T[];
}

// ---------------------------------------------------------------------------
// UploadCard
// ---------------------------------------------------------------------------

function UploadCard({
  item,
  timestamp,
  subtitle,
}: {
  item: UploadItem;
  timestamp: string | null;
  subtitle?: string;
}) {
  const displayTitle = item['processing.suggested_title'] ?? item.id;
  return (
    <Link
      to={`/processing/${item.id}`}
      state={{ from: '/my-ara' }}
      className="block rounded-xl border border-gray-100 bg-white p-4 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all group"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 p-2 rounded-lg bg-gray-100 group-hover:bg-emerald-50 transition-colors">
          <FileText className="w-4 h-4 text-gray-500 group-hover:text-emerald-600 transition-colors" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-emerald-700 transition-colors">
            {displayTitle}
          </p>
          {subtitle && (
            <p className="mt-1 text-xs text-gray-500 line-clamp-2">{subtitle}</p>
          )}
          <p className="mt-1.5 text-xs text-gray-400 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatTimeAgo(timestamp)}
          </p>
        </div>
      </div>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// RecentCard
// ---------------------------------------------------------------------------

function RecentCard({
  to,
  title,
  timestamp,
  icon: Icon,
}: {
  to: string;
  title: string;
  timestamp: string | null;
  icon: React.ElementType;
}) {
  return (
    <Link
      to={to}
      className="block rounded-xl border border-gray-100 bg-white p-4 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all group"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 p-2 rounded-lg bg-gray-100 group-hover:bg-emerald-50 transition-colors">
          <Icon className="w-4 h-4 text-gray-500 group-hover:text-emerald-600 transition-colors" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-emerald-700 transition-colors">
            {title}
          </p>
          <p className="mt-1.5 text-xs text-gray-400 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatTimeAgo(timestamp)}
          </p>
        </div>
      </div>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

function Panel({
  title,
  tooltip,
  icon: Icon,
  iconColor,
  emptyMessage,
  loading,
  error,
  isEmpty,
  children,
}: {
  title: string;
  tooltip: string;
  icon: React.ElementType;
  iconColor: string;
  emptyMessage: string;
  loading: boolean;
  error: string | null;
  isEmpty: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
      <div className="flex items-center gap-2 mb-5">
        <div className={`p-2 rounded-lg ${iconColor}`}>
          <Icon className="w-4 h-4" />
        </div>
        <h2 className="text-base font-bold text-gray-900">{title}</h2>
        <Tooltip content={tooltip}>
          <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
        </Tooltip>
      </div>

      {loading && (
        <div className="flex justify-center py-10">
          <Loader2 className="w-6 h-6 text-emerald-600 animate-spin" />
        </div>
      )}

      {!loading && error && (
        <p className="text-sm text-red-500 py-6 text-center">{error}</p>
      )}

      {!loading && !error && isEmpty && <EmptyState message={emptyMessage} />}

      {!loading && !error && !isEmpty && (
        <div className="space-y-3">
          {children}
        </div>
      )}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-gray-400 gap-2">
      <InboxIcon className="w-8 h-8" />
      <p className="text-sm">{message}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const BRAND_BG = 'bg-emerald-800';
const BRAND_TEXT = 'text-emerald-800';

export default function MyAra() {
  const navigate = useNavigate();
  const { getHref } = useApiRoot();
  const myAraHref = getHref('my');

  const [links, setLinks] = useState<MyAraLinks | null>(null);
  const [linksError, setLinksError] = useState<string | null>(null);

  const [pendingItems, setPendingItems] = useState<UploadItem[]>([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingError, setPendingError] = useState<string | null>(null);

  const [failedItems, setFailedItems] = useState<UploadItem[]>([]);
  const [failedLoading, setFailedLoading] = useState(false);
  const [failedError, setFailedError] = useState<string | null>(null);

  const [recentRecords, setRecentRecords] = useState<RecentRecord[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [recordsError, setRecordsError] = useState<string | null>(null);

  const [deletedRecords, setDeletedRecords] = useState<RecentRecord[]>([]);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [deletedError, setDeletedError] = useState<string | null>(null);

  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);
  const [searchesLoading, setSearchesLoading] = useState(false);
  const [searchesError, setSearchesError] = useState<string | null>(null);

  useEffect(() => {
    if (!myAraHref) return;

    let cancelled = false;
    setLinks(null);
    setLinksError(null);

    (async () => {
      try {
        const response = await apiFetch(myAraHref);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = (await response.json()) as { _links?: MyAraLinks };
        if (!cancelled) {
          setLinks(data._links ?? {});
        }
      } catch {
        if (!cancelled) {
          setLinksError('Could not load My ARA.');
          setLinks({});
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [myAraHref]);

  useEffect(() => {
    if (!links) return;

    const pendingHref = links.pending_uploads?.href;
    if (pendingHref) {
      setPendingLoading(true);
      setPendingError(null);
      fetchProcessList(pendingHref)
        .then(setPendingItems)
        .catch(() => setPendingError('Could not load pending uploads.'))
        .finally(() => setPendingLoading(false));
    }

    const failedHref = links.failed_uploads?.href;
    if (failedHref) {
      setFailedLoading(true);
      setFailedError(null);
      fetchProcessList(failedHref)
        .then(setFailedItems)
        .catch(() => setFailedError('Could not load upload errors.'))
        .finally(() => setFailedLoading(false));
    }

    const recordsHref = links.recent_records?.href;
    if (recordsHref) {
      setRecordsLoading(true);
      setRecordsError(null);
      fetchEmbeddedList<RecentRecord>(recordsHref, 'records')
        .then(setRecentRecords)
        .catch(() => setRecordsError('Could not load recent records.'))
        .finally(() => setRecordsLoading(false));
    }

    const deletedRecordsHref = links.deleted_records?.href;
    if (deletedRecordsHref) {
      setDeletedLoading(true);
      setDeletedError(null);
      fetchEmbeddedList<RecentRecord>(deletedRecordsHref, 'records')
        .then(setDeletedRecords)
        .catch(() => setDeletedError('Could not load deleted records.'))
        .finally(() => setDeletedLoading(false));
    }

    const searchesHref = links.recent_searches?.href;
    if (searchesHref) {
      setSearchesLoading(true);
      setSearchesError(null);
      fetchEmbeddedList<RecentSearch>(searchesHref, 'searches')
        .then(setRecentSearches)
        .catch(() => setSearchesError('Could not load recent searches.'))
        .finally(() => setSearchesLoading(false));
    }
  }, [links]);

  const linksLoading = Boolean(myAraHref) && links === null && !linksError;
  const showPending = Boolean(links?.pending_uploads);
  const showFailed = Boolean(links?.failed_uploads);
  const showRecords = Boolean(links?.recent_records);
  const showDeleted = Boolean(links?.deleted_records);
  const showSearches = Boolean(links?.recent_searches);

  return (
    <div className="flex-1 bg-gray-50/50 text-gray-900 font-sans selection:bg-emerald-100 selection:text-emerald-900">
      {/* Navbar */}
      <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20 items-center">
            <div
              className="flex items-center space-x-3 group cursor-pointer"
              onClick={() => navigate('/')}
            >
              <div
                className={`w-10 h-10 ${BRAND_BG} rounded-xl shadow-lg shadow-emerald-200 flex items-center justify-center text-white transform group-hover:scale-110 transition-transform duration-300`}
              >
                <Database className="w-5 h-5" />
              </div>
              <div>
                <span className={`text-xl font-bold tracking-tight ${BRAND_TEXT}`}>ARA</span>
                <span className="text-xs text-gray-400 block font-medium -mt-1 tracking-widest opacity-60">
                  Automated Records Assistant
                </span>
              </div>
            </div>
            <AppNavActions />
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Page header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">My ARA</h1>
        </div>

        {linksLoading && (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
          </div>
        )}

        {linksError && (
          <p className="text-sm text-red-500 py-10 text-center">{linksError}</p>
        )}

        {!linksLoading && !linksError && links && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {showPending && (
              <Panel
                title="Pending Uploads"
                tooltip="Pending uploads do not appear as records until they have been reviewed and confirmed."
                icon={Clock}
                iconColor="bg-purple-100 text-purple-700"
                emptyMessage="No uploads awaiting review"
                loading={pendingLoading}
                error={pendingError}
                isEmpty={pendingItems.length === 0}
              >
                {pendingItems.map((item) => (
                  <UploadCard
                    key={item.id}
                    item={item}
                    timestamp={item.completed_at ?? item.initiated_at}
                  />
                ))}
              </Panel>
            )}

            {showFailed && (
              <Panel
                title="Recent Upload Errors"
                tooltip="Uploads with errors that occurred during processing within the last 7 days. Must be re-uploaded and confirmed for this file to become a record."
                icon={AlertTriangle}
                iconColor="bg-amber-100 text-amber-700"
                emptyMessage="No recent upload errors"
                loading={failedLoading}
                error={failedError}
                isEmpty={failedItems.length === 0}
              >
                {failedItems.map((item) => (
                  <UploadCard
                    key={item.id}
                    item={item}
                    timestamp={item.failed_at ?? item.initiated_at}
                  />
                ))}
              </Panel>
            )}

            {showRecords && (
              <Panel
                title="Recent Records"
                tooltip="Records you have recently accessed. Click a card to open the record."
                icon={FileText}
                iconColor="bg-emerald-100 text-emerald-700"
                emptyMessage="No recently accessed records"
                loading={recordsLoading}
                error={recordsError}
                isEmpty={recentRecords.length === 0}
              >
                {recentRecords.map((record) => (
                  <RecentCard
                    key={record.id}
                    to={`/records/${record.id}`}
                    title={record.title ?? record.id}
                    timestamp={record.timestamp}
                    icon={FileText}
                  />
                ))}
              </Panel>
            )}

            {showSearches && (
              <Panel
                title="Recent Searches"
                tooltip="Searches you have recently run. Click a card to run that search again."
                icon={History}
                iconColor="bg-blue-100 text-blue-700"
                emptyMessage="No recent searches"
                loading={searchesLoading}
                error={searchesError}
                isEmpty={recentSearches.length === 0}
              >
                {recentSearches.map((search) => (
                  <RecentCard
                    key={search.id}
                    to={`/search?q=${encodeURIComponent(search.raw_query)}&rerun=1`}
                    title={search.raw_query}
                    timestamp={search.started_at}
                    icon={SearchIcon}
                  />
                ))}
              </Panel>
            )}

            {showDeleted && (
              <Panel
                title="Deleted Records"
                tooltip="Records you deleted that are still available to restore. Click a card to open the record."
                icon={Trash2}
                iconColor="bg-red-100 text-red-700"
                emptyMessage="No deleted records"
                loading={deletedLoading}
                error={deletedError}
                isEmpty={deletedRecords.length === 0}
              >
                {deletedRecords.map((record) => (
                  <RecentCard
                    key={record.id}
                    to={`/records/${record.id}`}
                    title={record.title ?? record.id}
                    timestamp={record.timestamp}
                    icon={Trash2}
                  />
                ))}
              </Panel>
            )}
          </div>
        )}
      </main>

      <footer className="bg-white border-t border-gray-200 mt-auto py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-gray-500">
          <p>&copy; 2026 Palmerston North City Council &bull; ARA</p>
        </div>
      </footer>
    </div>
  );
}
